import * as html2canvas from 'html2canvas';

import { Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Component, ViewChild, OnDestroy, ElementRef } from '@angular/core';

import { Subject, Observable } from 'rxjs';
import { tap, switchMap, finalize, take, map } from 'rxjs/operators';

import { isEmpty, get } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@service/toast.service';
import { QuotationService } from '@service/quotation.service';
import { AuthenticationService } from '@service/authentication.service';
import { NgbDateFormatterService } from '@service/ngb-date-formatter.service';

import { MasterData, InsurancePlan } from '@model/master-data.model';
import { Product, SelectedProduct, Package } from '@model/product-detail.model';
import { QuoteData, CreateQuoteRequest, Quotation, Agent, Coverages } from '@model/quotation.model';

import { ChannelType, StatusMode, getPriceErrorType, PackageTypeCatagory, PackageTypeCatagoryFull, TravelType } from '@enum/quotation.enum';

import { SendEmailComponent } from './send-email/send-email.component';
import { ProductDetailComponent } from './product-detail/product-detail.component';
import { CaptureScreenModalComponent } from './capture-screen-modal/capture-screen-modal.component';

@Component({
  selector: 'app-quotation',
  templateUrl: './quotation.component.html',
  styleUrls: ['./quotation.component.scss']
})
export class QuotationComponent implements OnDestroy {
  @ViewChild('quotationCaptureContent', { static: true }) quotationCaptureContent: ElementRef;
  @ViewChild(ProductDetailComponent, { static: true }) productDetailComponent: ProductDetailComponent;

  productDetailData: Product;
  getPriceErrorType: string;

  isLoading: boolean = false;

  statusMode: typeof StatusMode = StatusMode;
  currentMode: string = StatusMode.INITIAL;
  currentQuoteData: QuoteData;

  getPrictErrorType: typeof getPriceErrorType = getPriceErrorType;

  private onDestroy$: Subject<void> = new Subject();
  private HTML_TO_CANVAS_OPTION = {
    scale: 1.75,
    windowWidth: 1050,
    useCORS: true,
    logging: false,
  };

  constructor(
    private router: Router,
    private toastService: ToastService,
    private modalService: BsModalService,
    private translateService: TranslateService,
    private quotationService: QuotationService,
    private dateService: NgbDateFormatterService,
    private authenticationService: AuthenticationService
  ) {}

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onSubmit(mode: string): void {
    if (mode === StatusMode.EDIT) {
      this.currentMode = mode;
    } else {
      this.getProductPrice();
    }
  }

  async onCaptureImageClick(): Promise<void> {
    this.isLoading = true;

    const targetElement: HTMLElement = this.quotationCaptureContent.nativeElement;

    if (targetElement) {
      const canvasElement: HTMLCanvasElement = await html2canvas.default(targetElement, this.HTML_TO_CANVAS_OPTION);
      this.openCaptureScreenModal(canvasElement.toDataURL('image/png'));
    }

    this.isLoading = false;
  }

  onSendEmailClick(): void {
    this.modalService.show(SendEmailComponent, {
      class: 'modal-xl',
      ignoreBackdropClick: true
    });
  }

  onBuyNowClick(): void {
    this.getSelectedProduct()
      .pipe(
        take(1),
        tap(() => {
          this.isLoading = true;
        }),
        switchMap((quoteData: CreateQuoteRequest) => {
          return this.quotationService.createQuote(quoteData);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (quotes: Quotation[]) => {
          this.quotationService.changeCreateQuoteResult(quotes);
          this.router.navigate(['/customer-info']);
        },
        error: (err: any) => {
          if (err.error) {
            this.toastService.showErrorToastFromBackend(err.error, this.translateService.currentLang);
          }
        }
      });
  }

  getChannel(): string {
    if (this.authenticationService.getUserType() === 'INT') {
      return this.currentQuoteData.channelType;
    }
    return ChannelType.INTERMEDIARY;
  }

  getPackageTravelType(): PackageTypeCatagoryFull {
    switch (this.currentQuoteData.type.code) {
      case PackageTypeCatagory.INDIVIDUAL:
        return PackageTypeCatagoryFull.INDIVIDUAL;
      case PackageTypeCatagory.GROUP:
        return PackageTypeCatagoryFull.GROUP;
      case PackageTypeCatagory.FAMILY:
        return PackageTypeCatagoryFull.FAMILY;
      default:
        return PackageTypeCatagoryFull.INDIVIDUAL;
    }
  }

  getProductPrice(): void {
    this.quotationService
      .getQuoteData()
      .pipe(
        take(1),
        tap((quoteData: QuoteData) => {
          this.isLoading = true;
          this.getPriceErrorType = null;
          this.currentQuoteData = quoteData;
        }),
        switchMap((quoteData: QuoteData) => {
          return this.quotationService.getProductPrice(quoteData);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (product: Product) => {
          if (product.packages.length === 0) {
            this.getPriceErrorType = getPriceErrorType.NOT_FOUND;
          }

          this.currentMode = StatusMode.VIEW;
          this.productDetailData = product;
          this.productDetailComponent.setInitialSelectedProduct(product);
        },
        error: () => {
          this.currentMode = StatusMode.INITIAL;
          this.getPriceErrorType = getPriceErrorType.UNEXPECTED;
        }
      });
  }

  getSelectedProduct(): Observable<CreateQuoteRequest> {
    return this.quotationService.getSelectedProduct().pipe(
      take(1),
      map((product: SelectedProduct) => {
        return this.createQuotationData(product);
      })
    );
  }

  createQuotationData({ product }: SelectedProduct): CreateQuoteRequest {
    const {
      fromDate,
      toDate,
      from,
      to,
      travelType,
      durationPeriod,
      directDiscount,
      insurancePlan,
      type,
      schoolName,
      period,
      duration
    } = this.currentQuoteData;
    return {
      schoolName,
      channel: this.getChannel(),
      numberOfInsuredParty: this.getNumberOfInsuredParty(),
      contract: {
        subProductId: get(insurancePlan, 'subProductId', ''),
        agent: this.getAgentInfo(),
        coverages: this.getCoverages(type, travelType, insurancePlan, product),
        startDate: this.dateService.ngbDateToString(fromDate),
        endDate: this.dateService.ngbDateToString(toDate),
        computationPeriod: travelType,
        directDiscountGroup: get(directDiscount, 'directDiscountGroup', ''),
        fromCountryCode: get(from, 'code', null),
        toCountryCodes: !isEmpty(to) ? to.map((item: MasterData) => item.code) : [],
        travelPackageType: this.getPackageTravelType(),
        durationPeriod: travelType === TravelType.ANNUAL_TRIP ? parseInt(durationPeriod.code, 10) : duration
      }
    };
  }

  openCaptureScreenModal(captureCanvasData: string): void {
    this.modalService.show(CaptureScreenModalComponent, {
      class: 'modal-xl',
      initialState: {
        captureCanvasData
      }
    });
  }

  getAgentNumber(): string {
    if (this.authenticationService.getUserType() === 'INT' && this.currentQuoteData.channelType === ChannelType.INTERMEDIARY) {
      return this.currentQuoteData.imdCode.intermediaryId;
    }

    return this.authenticationService.currentUser.intermediaryId;
  }

  getAgentInfo(): Agent {
    const { aoMember } = this.currentQuoteData;

    return {
      aoMember: get(aoMember, 'aoMemberCode', null),
      agentNumber: this.getAgentNumber(),
      branchId: null,
      businessType: null,
      name: null,
      organizationalUnit: null,
      userId: null,
      marketingUser: null
    };
  }

  getCoverages(type: MasterData, travelType: TravelType, plan: InsurancePlan, product: Package): Coverages[] {
    return [
      {
        levelId: get(product, 'levelId', ''),
        topLevelId: plan.topLevelId,
        packageType: get(type, 'code', ''),
        isAnnualTrip: travelType === TravelType.ANNUAL_TRIP,
        packageId: get(product, 'packageId', '')
      }
    ];
  }

  getNumberOfInsuredParty(): number {
    const { numPeople } = this.currentQuoteData;

    return parseInt(get(numPeople, 'code', '0'), 10);
  }

  get showQuoteForm(): boolean {
    return this.currentMode !== this.statusMode.VIEW;
  }

  get showQuoteSummary(): boolean {
    return this.currentMode === this.statusMode.VIEW;
  }

  get showProductDetail(): boolean {
    return this.currentMode !== this.statusMode.INITIAL;
  }

  get showButtonGroup(): boolean {
    return this.currentMode !== this.statusMode.INITIAL && this.getPriceErrorType === null;
  }

  get disabled(): boolean {
    return this.currentMode === this.statusMode.EDIT;
  }
}
